import type { AdjudicationRun, AdjudicationTask } from "@/services/api/case-checking";

const NO_HUMAN_SHEET_RE = /no human sheet/i;

/** Routine adjudication with no human worksheet to compare against. */
export function isAgentOnlyAdjudication(run: AdjudicationRun): boolean {
  if (run.header.human_sheet_reviewer?.trim()) return false;
  if (run.rollup.human_overall_grade?.trim()) return false;
  if (run.rollup.human_review_routing === "no_formal_review") return true;
  if (run.rows.length === 0) return false;
  return run.rows.every((row) => NO_HUMAN_SHEET_RE.test(row.human_position));
}

const ACTIVE_TASK_STATUSES = new Set(["queued", "gated", "working"]);

/**
 * Where the newest adjudicate task stands relative to the newest saved run.
 *
 * - `saved`: a run was saved after the task was created (the task may still be wrapping up).
 * - `running`: the agent is queued or working.
 * - `no_result`: the task finished without saving a run.
 * - `failed`: the task failed, was blocked, or was closed.
 */
export type AdjudicationTaskOutcome = "idle" | "saved" | "running" | "no_result" | "failed";

export function adjudicationTaskOutcome(
  task: AdjudicationTask | null,
  latestRun: AdjudicationRun | null,
): AdjudicationTaskOutcome {
  if (!task) return "idle";
  if (latestRun && Date.parse(latestRun.created_at) >= Date.parse(task.created_at)) return "saved";
  if (ACTIVE_TASK_STATUSES.has(task.status)) return "running";
  return task.status === "done" ? "no_result" : "failed";
}
